import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  ChevronRight,
  Cookie,
  Info,
  LogOut,
  MessageCircle,
  ScrollText,
  Settings,
  Shield,
} from "lucide-react";
import PatientAppLayout from "../../components/PatientAppLayout/PatientAppLayout";
import API, { apiRequest } from "../../Api/Api";
import ENDPOINTS from "../../Api/Endpoints";
import { tokenManager } from "../../Api/tokenManager";
import { clearPatientUploadFlowState } from "../../utils/patientUploadFlowSession";
import { logout } from "../../Features/Postes/Slice/authSlice";
import { showConfirm } from "../../feedback/feedbackApi";
import DEFAULT_AVATAR from "../../Assets/profile.png";
import "../CompleteProfile/CompleteProfile.scss";
import "./PatientProfileMenuPage.scss";

/**
 * Patient “Menu” hub (`/patient-profile`): profile summary row, then policies/settings/account links.
 * Full edit flow lives at `/patient-profile/details`.
 */
export default function PatientProfileMenuPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [avatar, setAvatar] = useState(DEFAULT_AVATAR);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [profileLoaded, setProfileLoaded] = useState(
    () => !tokenManager.getTokenForRole("patient")
  );
  const [avatarFailed, setAvatarFailed] = useState(false);

  useEffect(() => {
    setAvatarFailed(false);
  }, [avatar]);

  useEffect(() => {
    const token = tokenManager.getTokenForRole("patient");
    if (!token) {
      setProfileLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get(ENDPOINTS.GET_ME, {
          params: { _t: Date.now() },
          headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
        });
        const body = res?.data ?? res;
        const user = body?.user ?? body?.data?.user ?? null;
        if (cancelled || !user) return;
        if (user.name) setName(String(user.name).trim());
        if (user.phone || user.mobile || user.mobileNo) {
          setPhone(String(user.phone || user.mobile || user.mobileNo).trim());
        }
        const av =
          user.avatar ||
          user.profileImageUrl ||
          user.profile_picture ||
          user.photoUrl ||
          body?.avatar ||
          body?.profileImageUrl ||
          "";
        if (av) setAvatar(String(av).trim());
      } catch {
        /* keep placeholders */
      } finally {
        if (!cancelled) setProfileLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const profileInitials = useMemo(
    () =>
      name
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((chunk) => chunk[0])
        .join("")
        .slice(0, 2)
        .toUpperCase() || "—",
    [name]
  );

  const displayName = name.trim() || "Your profile";
  const displayPhone = phone.trim() || "Add phone in profile";

  const handleLogout = useCallback(async () => {
    const confirmed = await showConfirm("Are you sure you want to log out?", {
      title: "Log out?",
      confirmText: "Log out",
      cancelText: "Stay",
      danger: true,
    });
    if (!confirmed) return;
    try {
      const tok = tokenManager.getTokenForRole("patient");
      if (tok) {
        await apiRequest({ method: "POST", url: ENDPOINTS.PATIENT_LOGOUT }).catch(() => {});
      }
    } finally {
      clearPatientUploadFlowState();
      dispatch(logout());
      navigate("/", { replace: true });
    }
  }, [dispatch, navigate]);

  return (
    <PatientAppLayout activeNav="profile">
      <div className="cp2Page cp2Page--app">
        <div className="cp2Flow cp2Flow--app">
          {!profileLoaded ? (
            <div className="cp2AppLoading" role="status" aria-live="polite">
              Loading…
            </div>
          ) : (
            <>
              <nav className="patient-profile-menu" aria-label="Menu — profile, policies, and account">
                <ul className="patient-profile-menu__list">
                  <li>
                    <Link
                      to="/patient-profile/details"
                      className="patient-profile-menu__profile-tile"
                      aria-label={`${displayName}. ${displayPhone}. Open profile details.`}
                    >
                      <div className="patient-profile-menu__avatar-wrap" aria-hidden>
                        {!avatarFailed ? (
                          <img
                            className="patient-profile-menu__avatar-img"
                            src={avatar}
                            alt=""
                            onError={() => setAvatarFailed(true)}
                          />
                        ) : (
                          <span className="patient-profile-menu__avatar-fallback">{profileInitials}</span>
                        )}
                      </div>
                      <div className="patient-profile-menu__profile-body">
                        <span className="patient-profile-menu__profile-name">{displayName}</span>
                        <span className="patient-profile-menu__profile-meta">{displayPhone}</span>
                      </div>
                      <ChevronRight
                        className="patient-profile-menu__chevron"
                        size={22}
                        strokeWidth={2}
                        aria-hidden
                      />
                    </Link>
                  </li>
                  <li className="patient-profile-menu__actionsSlot">
                    <div className="patient-profile-menu__sheet" role="group" aria-label="Policies, settings, and account">
                      <Link
                        to="/patient-profile/terms"
                        className="patient-profile-menu__row patient-profile-menu__row--link"
                      >
                        <span className="patient-profile-menu__row-icon" aria-hidden>
                          <ScrollText size={20} strokeWidth={1.85} />
                        </span>
                        <span className="patient-profile-menu__row-label">Terms &amp; conditions</span>
                        <ChevronRight className="patient-profile-menu__chevron" size={22} strokeWidth={2} aria-hidden />
                      </Link>
                      <Link
                        to="/patient-profile/policy"
                        className="patient-profile-menu__row patient-profile-menu__row--link"
                      >
                        <span className="patient-profile-menu__row-icon" aria-hidden>
                          <Shield size={20} strokeWidth={1.85} />
                        </span>
                        <span className="patient-profile-menu__row-label">Privacy policy</span>
                        <ChevronRight className="patient-profile-menu__chevron" size={22} strokeWidth={2} aria-hidden />
                      </Link>
                      <Link
                        to="/patient-profile/cookies"
                        className="patient-profile-menu__row patient-profile-menu__row--link"
                      >
                        <span className="patient-profile-menu__row-icon" aria-hidden>
                          <Cookie size={20} strokeWidth={1.85} />
                        </span>
                        <span className="patient-profile-menu__row-label">Cookies</span>
                        <ChevronRight className="patient-profile-menu__chevron" size={22} strokeWidth={2} aria-hidden />
                      </Link>
                      <Link
                        to="/patient-profile/settings"
                        className="patient-profile-menu__row patient-profile-menu__row--link"
                      >
                        <span className="patient-profile-menu__row-icon" aria-hidden>
                          <Settings size={20} strokeWidth={1.85} />
                        </span>
                        <span className="patient-profile-menu__row-label">Settings</span>
                        <ChevronRight className="patient-profile-menu__chevron" size={22} strokeWidth={2} aria-hidden />
                      </Link>
                      <Link
                        to="/patient-profile/app-version"
                        className="patient-profile-menu__row patient-profile-menu__row--link"
                      >
                        <span className="patient-profile-menu__row-icon" aria-hidden>
                          <Info size={20} strokeWidth={1.85} />
                        </span>
                        <span className="patient-profile-menu__row-label">App version</span>
                        <ChevronRight className="patient-profile-menu__chevron" size={22} strokeWidth={2} aria-hidden />
                      </Link>
                      <Link
                        to="/patient-support"
                        className="patient-profile-menu__row patient-profile-menu__row--link patient-profile-menu__row--sectionBreak"
                        aria-label="Chat with admin — open help assistant"
                      >
                        <span className="patient-profile-menu__row-icon" aria-hidden>
                          <MessageCircle size={20} strokeWidth={1.85} />
                        </span>
                        <span className="patient-profile-menu__row-label">Chat with admin</span>
                        <ChevronRight className="patient-profile-menu__chevron" size={22} strokeWidth={2} aria-hidden />
                      </Link>
                      <button type="button" className="patient-profile-menu__row patient-profile-menu__row--logout" onClick={handleLogout}>
                        <span className="patient-profile-menu__row-icon" aria-hidden>
                          <LogOut size={20} strokeWidth={1.85} />
                        </span>
                        <span className="patient-profile-menu__row-label">Log out</span>
                      </button>
                    </div>
                  </li>
                </ul>
              </nav>
            </>
          )}
        </div>
      </div>
    </PatientAppLayout>
  );
}
